import React, { useState } from 'react'
import BlogCard from './BlogCard'
import BlogModal from './BlogModal'

interface BlogPost {
  id: number
  image: string
  title: string
  readTime: string
  category: string
  author?: string
  date?: string
  content: string
}

const BlogSection: React.FC = () => {
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const blogPosts: BlogPost[] = [
    {
      id: 1,
      image: '/src/assets/placeholder-two.svg',
      title: 'Understanding Corporation Tax Deadlines: A Complete Guide for UK Businesses',
      readTime: '6 min read',
      category: 'Corporation Tax',
      author: 'BritFiler Team',
      date: '15 January 2025',
      content: `Understanding Corporation Tax Deadlines: A Complete Guide for UK Businesses

Corporation Tax is a fundamental obligation for all UK limited companies. Missing deadlines can result in significant penalties and interest charges. This comprehensive guide will help you understand everything you need to know about Corporation Tax deadlines and how to stay compliant.

What is Corporation Tax?

Corporation Tax is a tax on the profits of limited companies and other organisations in the UK. The current rate is 25% for profits over £250,000, with a lower rate of 19% for profits up to £50,000, and a marginal rate for profits between £50,000 and £250,000.

Key Deadlines You Need to Know

1. Payment Deadline
   - Corporation Tax payment is due 9 months and 1 day after the end of your accounting period
   - For example, if your accounting period ends on 31 December 2024, payment is due by 1 October 2025

2. Filing Deadline
   - The CT600 return must be filed within 12 months of the end of your accounting period
   - Using the same example, if your period ends 31 December 2024, the return must be filed by 31 December 2025

3. Accounting Period End
   - Your accounting period is typically 12 months, but can be shorter in the first year or when changing your year-end

Common Mistakes to Avoid

Many businesses fall into these common traps:

• Confusing payment and filing deadlines - remember, payment comes first
• Not accounting for bank holidays or weekends when calculating deadlines
• Forgetting to account for estimated tax payments
• Missing the deadline for amended returns

How BritFiler Can Help

BritFiler's automated deadline tracking system ensures you never miss a Corporation Tax deadline. Our platform:

• Sends proactive reminders well in advance of deadlines
• Tracks multiple companies and their respective deadlines
• Provides a clear dashboard showing all upcoming obligations
• Integrates directly with HMRC for seamless filing

Best Practices

1. Set up calendar reminders at least 3 months before deadlines
2. Prepare your accounts well in advance
3. Use digital filing tools to reduce errors
4. Keep accurate records throughout the year
5. Consider working with an accountant for complex situations

Conclusion

Staying on top of Corporation Tax deadlines is crucial for maintaining compliance and avoiding penalties. With proper planning and the right tools, managing your Corporation Tax obligations can be straightforward and stress-free.

Ready to streamline your Corporation Tax filing? Get started with BritFiler today and never miss a deadline again.`
    },
    {
      id: 2,
      image: '/src/assets/placeholder-two.svg',
      title: 'Self Assessment Made Simple: A Step-by-Step Guide for First-Time Filers',
      readTime: '8 min read',
      category: 'Self Assessment',
      author: 'BritFiler Team',
      date: '12 January 2025',
      content: `Self Assessment Made Simple: A Step-by-Step Guide for First-Time Filers

Filing your first Self Assessment tax return can feel overwhelming, but it doesn't have to be. This guide will walk you through the entire process, from determining if you need to file to submitting your return successfully.

Who Needs to File a Self Assessment?

You must file a Self Assessment if you:
• Are self-employed with income over £1,000
• Are a company director
• Have income from property over £2,500
• Have untaxed income from investments or savings
• Have received income from abroad
• Have been asked to file by HMRC

Key Deadlines

• Registration: 5 October (if you're new to Self Assessment)
• Paper returns: 31 October
• Online returns: 31 January
• Payment: 31 January

Step-by-Step Filing Process

Step 1: Register for Self Assessment
If you're filing for the first time, register with HMRC by 5 October. You'll receive a Unique Taxpayer Reference (UTR) and can set up your Government Gateway account.

Step 2: Gather Your Documents
You'll need:
• P60 or P45 from employment
• Bank statements
• Invoices and receipts
• Records of expenses
• Details of any other income
• Records of pension contributions
• Charitable donation receipts

Step 3: Calculate Your Income
Add up all your income sources:
• Employment income
• Self-employment income
• Property income
• Investment income
• Any other taxable income

Step 4: Calculate Allowable Expenses
For self-employed individuals, you can deduct:
• Office costs
• Travel expenses
• Clothing expenses (uniforms only)
• Staff costs
• Materials and stock
• Financial costs
• Marketing and advertising
• Training costs

Step 5: Complete Your Return
Use HMRC's online service or software like BritFiler to complete your SA100 form. The form will guide you through each section.

Step 6: Review and Submit
Double-check all figures before submitting. Common errors include:
• Transposing numbers
• Missing income sources
• Incorrect expense claims
• Missing National Insurance contributions

Step 7: Pay What You Owe
Payment is due by 31 January. You can pay:
• Online via bank transfer
• By debit or credit card
• Through your bank
• By cheque

Tips for First-Time Filers

1. Start Early: Don't wait until January to start gathering documents
2. Keep Records: Maintain good records throughout the year
3. Use Software: Digital tools reduce errors and save time
4. Seek Help: Don't hesitate to contact HMRC or use professional services
5. Set Aside Money: Budget for your tax bill throughout the year

Common Questions

Q: What if I miss the deadline?
A: You'll face penalties. File as soon as possible to minimise charges.

Q: Can I claim expenses?
A: Yes, if you're self-employed, you can claim legitimate business expenses.

Q: What if I make a mistake?
A: You can amend your return within 12 months of the filing deadline.

How BritFiler Simplifies Self Assessment

BritFiler's guided wizard makes Self Assessment straightforward:
• Step-by-step guidance through each section
• Automatic calculations
• Real-time validation
• Direct HMRC integration
• Secure document storage
• Deadline reminders

Conclusion

Filing your Self Assessment doesn't have to be complicated. With proper preparation and the right tools, you can complete your return accurately and on time. Start early, keep good records, and consider using digital filing software to make the process smoother.

Ready to file your Self Assessment? Try BritFiler's guided wizard today and experience the simplest way to complete your tax return.`
    },
    {
      id: 3,
      image: '/src/assets/placeholder-two.svg',
      title: 'Confirmation Statements Explained: What Every UK Company Director Needs to Know',
      readTime: '5 min read',
      category: 'Confirmation Statements',
      author: 'BritFiler Team',
      date: '10 January 2025',
      content: `Confirmation Statements Explained: What Every UK Company Director Needs to Know

As a company director, staying compliant with Companies House requirements is essential. One of the most important annual obligations is the Confirmation Statement. This guide explains everything you need to know.

What is a Confirmation Statement?

A Confirmation Statement (formerly called an Annual Return) is a filing that confirms your company's information is up to date with Companies House. It's not a financial statement, but rather a snapshot of your company's current details.

What Information is Included?

The Confirmation Statement confirms:
• Registered office address
• Directors' details
• Shareholders' information
• Share capital structure
• People with significant control (PSCs)
• Standard Industrial Classification (SIC) codes
• Trading status

When is it Due?

You must file a Confirmation Statement at least once every 12 months. The deadline is:
• 12 months from your incorporation date, or
• 12 months from your last Confirmation Statement

You can file early, but you cannot file late without consequences.

Filing Requirements

1. Check Your Information
   Before filing, verify all information is correct:
   • Are all directors still active?
   • Have there been any address changes?
   • Has share capital changed?
   • Are PSC details accurate?

2. Update if Necessary
   If anything has changed, you must update it before or when filing your Confirmation Statement.

3. Submit the Statement
   You can file online through Companies House or use filing software like BritFiler.

What Happens if You Miss the Deadline?

Failing to file your Confirmation Statement on time can result in:
• Late filing penalties
• Potential prosecution
• Company being struck off the register
• Directors being disqualified

The penalty for late filing is currently £150, which increases to £1,500 if you're more than 6 months late.

Common Mistakes to Avoid

• Forgetting the deadline - set reminders
• Not updating changed information
• Incorrect director details
• Missing PSC information
• Wrong share capital figures

How to File Your Confirmation Statement

Option 1: Direct with Companies House
• Log into your Companies House account
• Complete the online form
• Pay the £13 filing fee
• Submit

Option 2: Using BritFiler
• Log into your BritFiler account
• Select your company
• Review and confirm information
• Submit directly to Companies House
• Receive confirmation

Benefits of Using Filing Software

Using software like BritFiler offers several advantages:
• Automated deadline reminders
• Pre-filled information from your company record
• Validation to prevent errors
• Direct integration with Companies House
• Secure storage of filing history
• Multi-company management

Best Practices

1. Set Annual Reminders: Don't rely on Companies House reminders alone
2. Keep Records Updated: Update changes as they happen, not just at filing time
3. Review Before Filing: Always double-check information before submission
4. File Early: You can file up to 14 days before your deadline
5. Keep Confirmations: Save your filing confirmation for your records

Changes You Must Report

Certain changes must be reported separately and cannot wait for your Confirmation Statement:
• Change of registered office (within 14 days)
• Appointment or resignation of directors (within 14 days)
• Change of company name (requires special resolution)
• Changes to share capital (within one month)

Integration with Other Filings

Your Confirmation Statement works alongside other annual obligations:
• Annual accounts filing
• Corporation Tax return
• VAT returns (if applicable)

Coordinating these filings can help ensure nothing is missed.

Conclusion

Filing your Confirmation Statement is a straightforward but essential annual requirement. By staying organised, setting reminders, and using the right tools, you can ensure compliance without stress.

BritFiler makes Confirmation Statement filing simple with automated reminders, pre-filled forms, and direct Companies House integration. Never miss a deadline again.

Ready to streamline your company filings? Get started with BritFiler today and manage all your Companies House obligations in one place.`
    },
    {
      id: 4,
      image: '/src/assets/placeholder-two.svg',
      title: 'iXBRL Tagging: Why It Matters for Your Company Accounts Filing',
      readTime: '7 min read',
      category: 'iXBRL Tagging',
      author: 'BritFiler Team',
      date: '8 January 2025',
      content: `iXBRL Tagging: Why It Matters for Your Company Accounts Filing

If you're filing company accounts with Companies House, you've likely heard about iXBRL. But what exactly is it, and why does it matter? This guide explains everything you need to know.

What is iXBRL?

iXBRL (Inline eXtensible Business Reporting Language) is a format that combines human-readable HTML with machine-readable XBRL tags. It allows financial data to be both displayed as a normal document and processed by computers for analysis and validation.

Why is iXBRL Required?

Since 2011, Companies House has required all company accounts to be filed in iXBRL format. This requirement applies to:
• Full accounts
• Abbreviated accounts (for small companies)
• Micro-entity accounts
• Dormant company accounts

The Benefits of iXBRL

1. Standardisation
   iXBRL creates a standard format that makes it easier to compare financial data across companies and industries.

2. Automation
   Tagged data can be automatically processed, reducing manual data entry and errors.

3. Transparency
   Financial information becomes more accessible and searchable.

4. Compliance
   Filing in the correct format ensures your accounts are accepted by Companies House.

How iXBRL Tagging Works

iXBRL tags are applied to specific elements in your financial statements:
• Balance sheet items (assets, liabilities, equity)
• Profit and loss items (revenue, expenses)
• Notes to the accounts
• Directors' reports

Each tag corresponds to a specific element in the UK GAAP or IFRS taxonomy, ensuring consistency across all filings.

Common Tagging Challenges

1. Choosing the Right Tag
   With thousands of tags available, selecting the correct one can be challenging.

2. Complex Structures
   Some financial structures require multiple tags or custom extensions.

3. Validation Errors
   Incorrect tagging can lead to rejection by Companies House.

4. Time Consumption
   Manual tagging is time-consuming and error-prone.

The Solution: Automated Tagging

Modern filing software like BritFiler automates the iXBRL tagging process:
• Automatic tag selection based on your accounts
• Validation before submission
• Compliance with UK GAAP and IFRS standards
• Error detection and correction suggestions

What Gets Tagged?

Typical elements that require tagging include:
• Company information
• Accounting policies
• Balance sheet items
• Profit and loss items
• Cash flow statement items
• Notes to the accounts
• Directors' remuneration
• Related party transactions

Validation and Quality Checks

Before submission, your iXBRL file should be validated:
• Structure validation
• Taxonomy compliance
• Business rule checks
• Mathematical accuracy

BritFiler performs all these checks automatically, ensuring your accounts are ready for submission.

Filing Process with iXBRL

1. Prepare Your Accounts
   Create your financial statements in your accounting software.

2. Generate iXBRL
   Use software to convert your accounts to iXBRL format.

3. Validate
   Check for errors and ensure compliance.

4. Submit
   File with Companies House through their online service or via filing software.

5. Receive Confirmation
   Companies House will confirm receipt and acceptance.

Best Practices

1. Use Professional Software: Don't attempt manual tagging
2. Validate Early: Check your iXBRL file before the deadline
3. Keep Backups: Save your tagged files for future reference
4. Review Tags: Ensure tags accurately represent your financial data
5. Stay Updated: Taxonomies are updated regularly

Common Mistakes

• Using incorrect tags
• Missing required tags
• Incorrect tag values
• Structural errors
• Validation failures

How BritFiler Handles iXBRL

BritFiler's automated iXBRL generation:
• Converts your accounts automatically
• Applies correct tags based on UK GAAP
• Validates before submission
• Provides error reports if issues are found
• Submits directly to Companies House
• Stores your tagged files securely

Integration with Accounting Software

Many accounting packages can export to iXBRL, but using integrated filing software provides:
• Better validation
• Direct submission
• Error correction
• Filing history
• Multi-company management

Cost Considerations

While iXBRL filing can seem complex, using the right software:
• Saves time (hours vs. minutes)
• Reduces errors
• Ensures compliance
• Prevents rejection fees
• Provides peace of mind

Conclusion

iXBRL tagging is an essential requirement for filing company accounts with Companies House. While it may seem technical, modern software makes the process straightforward and automated.

By using BritFiler, you can ensure your accounts are properly tagged, validated, and submitted without the complexity. Focus on running your business while we handle the technical requirements.

Ready to simplify your accounts filing? Try BritFiler's automated iXBRL generation today and experience hassle-free company accounts submission.`
    },
  ]

  const handleCardClick = (post: BlogPost) => {
    setSelectedPost(post)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedPost(null)
  }

  return (
    <>
      <section className='py-20 px-4 sm:px-6 lg:px-20 bg-white'>
        <div className='w-full mx-auto'>
          {/* Header */}
          <div className='text-center mb-16'>
            <h2 className='text-4xl font-bold text-gray-900 mb-4'>
              Tax Filing Insights & Guides
            </h2>
            <p className='text-lg text-gray-600 mb-8 max-w-2xl mx-auto'>
              Expert advice and news from our blog to stay up-to-date on UK tax
              filing, compliance, and best practices.
            </p>
          </div>

          {/* Blog Grid */}
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
            {blogPosts.map(post => (
              <BlogCard
                key={post.id}
                image={post.image}
                title={post.title}
                readTime={post.readTime}
                category={post.category}
                onClick={() => handleCardClick(post)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Blog Modal */}
      <BlogModal
        post={selectedPost}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  )
}

export default BlogSection
